import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag, Badge, FormControl, FormLabel,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import {
  ImmSolicitud, listarSolicitudes, obtenerSolicitud, eliminarSolicitud,
  vincularExpedienteSolicitud, listarLocalidades, Localidad,
} from '../../../api/immApi';
import { fmt, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge, ExpedienteSelect } from '../_shared';
import FormularioSolicitud from './FormularioSolicitud';

const TablaSolicitudes: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const vincularD = useDisclosure();
  const bajaD = useDisclosure();

  const [data, setData] = useState<ImmSolicitud[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [selected, setSelected] = useState<ImmSolicitud | null>(null);
  const [detalle, setDetalle] = useState<ImmSolicitud | null>(null);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(undefined);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroLocalidad, setFiltroLocalidad] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarSolicitudes());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); listarLocalidades().then(setLocalidades).catch(() => {}); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchLocalidad = filtroLocalidad ? item.localidad === filtroLocalidad : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? item.folio?.toLowerCase().includes(q) ||
        item.tipoApoyo?.toLowerCase().includes(q)
      : true;
    return matchEstatus && matchLocalidad && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const abrirVer = async (item: ImmSolicitud) => {
    setSelected(item);
    setDetalle(item);
    verD.onOpen();
    try { setDetalle(await obtenerSolicitud(item.id)); } catch { /* mantiene item base */ }
  };

  const abrirVincular = (item: ImmSolicitud) => {
    setSelected(item);
    setExpedienteId(item.expedienteId ?? undefined);
    vincularD.onOpen();
  };

  const handleVincularConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await vincularExpedienteSolicitud(selected.id, { expedienteId });
      toast({ title: 'Solicitud vinculada a expediente', status: 'success', duration: 3000 });
      vincularD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo vincular la solicitud.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleBajaConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await eliminarSolicitud(selected.id);
      toast({ title: 'Solicitud eliminada', status: 'success', duration: 3000 });
      bajaD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar la solicitud.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Folio': item.folio,
      'Fecha': fmt(item.fecha),
      'Tipo de apoyo': item.tipoApoyo || '',
      'Localidad': item.localidad || '',
      'Atención inmediata': item.atencionInmediata ? 'Sí' : 'No',
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Solicitudes IMM');
    XLSX.writeFile(wb, `Solicitudes_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por folio o tipo de apoyo..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '260px' }} />
          <Select placeholder="Localidad" value={filtroLocalidad} onChange={(e) => { setFiltroLocalidad(e.target.value); setCurrentPage(1); }} size="sm" w="170px">
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="150px">
            <option value="Recibida">Recibida</option>
            <option value="Vinculada">Vinculada</option>
            <option value="Atendida">Atendida</option>
            <option value="Cerrada">Cerrada</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nueva solicitud</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Folio</Th>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Tipo de apoyo</Th>
              <Th color="white" fontSize="xs">Localidad</Th>
              <Th color="white" fontSize="xs">Inmediata</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">No se encontraron solicitudes</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{item.folio}</Tag></Td>
                  <Td>{fmt(item.fecha)}</Td>
                  <Td><Text noOfLines={1} fontSize="sm">{item.tipoApoyo || '—'}</Text></Td>
                  <Td>{item.localidad || '—'}</Td>
                  <Td>{item.atencionInmediata ? <Badge colorScheme="red" fontSize="xs" borderRadius="full" px={2}>Inmediata</Badge> : '—'}</Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrirVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button>
                      {item.estatus !== 'Vinculada' && !item.expedienteId && (
                        <Button size="xs" colorScheme="purple" variant="outline" onClick={() => abrirVincular(item)}>Vincular</Button>
                      )}
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelected(item); bajaD.onOpen(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver (detalle read-only) */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Solicitud</ModalHeader>
          <ModalBody py={5}>{detalle && <FormularioSolicitud modo="view" data={detalle} onCancelar={verD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar solicitud</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioSolicitud modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nueva */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nueva solicitud</ModalHeader>
          <ModalBody py={5}><FormularioSolicitud modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Vincular a expediente */}
      <Modal isOpen={vincularD.isOpen} onClose={vincularD.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Vincular a expediente</ModalHeader>
          <ModalBody py={5}>
            <FormControl>
              <FormLabel fontSize="sm">Expediente</FormLabel>
              <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} />
            </FormControl>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Si no seleccionas un expediente, se creará uno nuevo con los datos de la solicitud.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={vincularD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleVincularConfirmar} isLoading={procesando} loadingText="Vinculando...">Vincular</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Eliminar (físico) */}
      <AlertDialog isOpen={bajaD.isOpen} leastDestructiveRef={cancelRef} onClose={bajaD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Eliminar solicitud</AlertDialogHeader>
          <AlertDialogBody>
            La solicitud <strong>{selected?.folio}</strong> se eliminará de forma permanente. ¿Continuar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={bajaD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleBajaConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaSolicitudes;
